import { ApprovalStatus, Config, UserRole } from "@employee-management/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Textarea } from "@/components/ui/textarea";
import { apiService } from "@/lib/api-service";
import { DatePicker } from "@/components/ui/date-picker";
import { subDays } from "date-fns";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { onKeyDownNumberOnly } from "@/lib/number-input.util";
import { useConfirmationDialog } from "@/lib/confirmation-dialog";

const overtimeEmployeeSchemaWithFinancial = z.object({
  employeeId: z.string().cuid("Employee ID must be selected"),
  hours: z.number().positive("Hours must be positive"),
  rate: z.number().positive("Rate must be positive").optional(),
  total: z.number().positive("Total must be positive").optional(),
  notes: z.string().optional(),
});

const overtimeEmployeeSchemaWithoutFinancial = z.object({
  employeeId: z.string().cuid("Employee ID must be selected"),
  hours: z.number().positive("Hours must be positive"),
  notes: z.string().optional(),
});

const overtimeFormSchemaWithFinancial = z.object({
  projectId: z.string().min(1, "Project ID is required"),
  notes: z.string().optional(),
  date: z.string().min(1, "Date is required"),
  approvalStatus: z.nativeEnum(ApprovalStatus),
  employees: z
    .array(overtimeEmployeeSchemaWithFinancial)
    .min(1, "At least one employee is required"),
});

const overtimeFormSchemaWithoutFinancial = z.object({
  projectId: z.string().min(1, "Project ID is required"),
  notes: z.string().optional(),
  date: z.string().min(1, "Date is required"),
  approvalStatus: z.nativeEnum(ApprovalStatus),
  employees: z
    .array(overtimeEmployeeSchemaWithoutFinancial)
    .min(1, "At least one employee is required"),
});

type OvertimeFormData = z.infer<typeof overtimeFormSchemaWithFinancial>;

interface OvertimeCreateDialogProps {
  projectId: string;
  projectMembers: Array<{
    id: string;
    name: string;
    email: string;
    department: string;
    hourlyRate: number;
  }>;
  disabled?: boolean;
}

export const OvertimeCreateDialog = ({
  projectId,
  projectMembers,
  disabled = false,
}: OvertimeCreateDialogProps) => {
  const { user, hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { showConfirmation } = useConfirmationDialog();

  // Determine if user can see financial information
  const canSeeFinancialInfo = hasRole(UserRole.MANAGER);

  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    getFieldState,
  } = useForm<OvertimeFormData>({
    resolver: zodResolver(
      canSeeFinancialInfo
        ? overtimeFormSchemaWithFinancial
        : overtimeFormSchemaWithoutFinancial
    ),
    defaultValues: {
      projectId,
      notes: "",
      date: new Date().toISOString().split("T")[0], // Today's date
      approvalStatus: ApprovalStatus.PENDING,
      employees: [
        {
          employeeId: "",
          hours: 0,
          rate: 0,
          total: 0,
          notes: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "employees",
  });

  const createOvertimeMutation = useMutation({
    mutationFn: async (data: OvertimeFormData) => {
      // Convert string date to Date object for the backend
      const overtimeData = {
        ...data,
        date: new Date(data.date),
        // For supervisors, we need to calculate rate and total on the backend
        employees: data.employees.map((employee) => ({
          ...employee,
          // If user can't see financial info, don't send rate/total
          ...(canSeeFinancialInfo ? {} : { rate: undefined, total: undefined }),
        })),
      };

      await apiService.post("/overtime", overtimeData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["overtime", "project", projectId],
      });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });
      reset();
      setOpen(false);
      toast.success("Overtime entry created successfully");
    },
    onError: (error: unknown) => {
      parseValidationErrors<OvertimeFormData>(error, setError, getFieldState);

      toast.error("Failed to create overtime entry");
    },
  });

  // Only supervisors can create overtime entries
  if (!hasRole(UserRole.SUPERVISOR)) {
    return null;
  }

  const onSubmit = (data: unknown) => {
    if (user?.role === UserRole.SUPERVISOR) {
      showConfirmation({
        title: "Create Overtime Entry",
        description:
          "Are you sure you want to create this overtime entry? Once created, it cannot be edited.",
        type: "warning",
        confirmText: "Create",
        cancelText: "Cancel",
        onConfirm: () => {
          createOvertimeMutation.mutate(data as OvertimeFormData);
        },
      });
    } else {
      createOvertimeMutation.mutate(data as OvertimeFormData);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Clear form when dialog is closed
      reset();
      // Reset field array to initial state
      remove();
      append({
        employeeId: "",
        hours: 0,
        rate: 0,
        total: 0,
        notes: "",
      });
    }
  };

  const addEmployee = () => {
    append({
      employeeId: "",
      hours: 0,
      rate: 0,
      total: 0,
      notes: "",
    });
  };

  const removeEmployee = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const employees = useWatch({
    name: "employees",
    control,
  });

  const selectedEmployeeIds = useMemo(
    () => employees.map((emp) => emp.employeeId).filter(Boolean),
    [employees]
  );

  const canAddMore = employees.length < projectMembers.length;

  const availableEmployeeOptions: ComboboxOption[] = projectMembers.map(
    (member) => ({
      value: member.id,
      label: member.name,
      description: `${member.department} • ${member.email}`,
      disabled: selectedEmployeeIds.includes(member.id),
    })
  );

  // Calculate total for an employee
  const calculateTotal = useCallback(
    (index: number) => {
      const employee = watch(`employees.${index}`);

      if (Number(employee?.hours) >= 0 && Number(employee?.rate) >= 0) {
        const total = Number(employee.hours) * Number(employee.rate);
        setValue(`employees.${index}.total`, total);
        return total;
      }
      return 0;
    },
    [setValue, watch]
  );

  // Auto-fill rate when employee is selected
  const handleEmployeeSelect = (index: number, id: string) => {
    const member = projectMembers.find((m) => m.id === id);
    if (member) {
      setValue(`employees.${index}.rate`, member.hourlyRate);
      calculateTotal(index);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          disabled={disabled}
          title={
            disabled
              ? "Cannot add overtime entries to non-active projects"
              : "Add overtime entry"
          }
        >
          <Plus className="h-4 w-4" />
          Add Overtime Entry
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Overtime Entry</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-6">
            {/* Basic overtime information */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label required>Date</Label>
                <DatePicker
                  date={new Date(watch("date"))}
                  onDateChange={(date) =>
                    setValue("date", date?.toISOString() || "")
                  }
                  slotProps={{
                    calendar: {
                      disabled: hasRole(UserRole.MANAGER)
                        ? undefined
                        : { before: subDays(new Date(), 3) },
                    },
                  }}
                  className="w-full"
                />
                {errors.date?.message && (
                  <p className="text-sm text-destructive">
                    {errors.date.message as string}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Overtime Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Enter overtime notes"
                rows={3}
              />
              {errors.notes?.message && (
                <p className="text-sm text-destructive">
                  {errors.notes.message as string}
                </p>
              )}
            </div>

            <Separator />

            {/* Employees section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Employees</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEmployee}
                  disabled={!canAddMore}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Employee
                </Button>
              </div>
              {!canAddMore && (
                <p className="text-sm text-muted-foreground">
                  All project members have been added to this overtime entry.
                </p>
              )}
              {fields.map((field, index) => {
                const currentEmployee = employees[index];

                return (
                  <Card className="!bg-transparent" key={field.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg -mt-1">
                          Employee {index + 1}
                        </CardTitle>
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeEmployee(index)}
                            className="text-destructive hover:text-destructive/90"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label required>Employee</Label>
                          <Combobox
                            options={availableEmployeeOptions}
                            value={currentEmployee?.employeeId || ""}
                            onValueChange={(value: string) => {
                              setValue(`employees.${index}.employeeId`, value);
                              handleEmployeeSelect(index, value);
                            }}
                            placeholder="Select employee"
                          />
                          {errors.employees?.[index]?.employeeId?.message && (
                            <p className="text-sm text-destructive">
                              {
                                errors.employees[index].employeeId
                                  .message as string
                              }
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label required>Hours</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...register(`employees.${index}.hours`, {
                              valueAsNumber: true,
                              onChange: () => calculateTotal(index),
                            })}
                            placeholder="0"
                            onKeyDown={onKeyDownNumberOnly}
                          />
                          {errors.employees?.[index]?.hours?.message && (
                            <p className="text-sm text-destructive">
                              {errors.employees[index].hours.message as string}
                            </p>
                          )}
                        </div>
                      </div>

                      {canSeeFinancialInfo && (
                        <div
                          className={`grid gap-4 grid-cols-1 sm:grid-cols-2`}
                        >
                          {canSeeFinancialInfo && (
                            <>
                              <div className="space-y-2">
                                <Label required>
                                  Rate ({Config.i18n.currency}/hr)
                                </Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  {...register(`employees.${index}.rate`, {
                                    valueAsNumber: true,
                                    onChange: () => calculateTotal(index),
                                  })}
                                  placeholder="0"
                                  startContent={
                                    <span className="text-muted-foreground">
                                      {Config.i18n.currency}
                                    </span>
                                  }
                                  endContent={
                                    <span className="text-muted-foreground text-xs w-4">
                                      /hr
                                    </span>
                                  }
                                  onKeyDown={onKeyDownNumberOnly}
                                />
                                {errors.employees?.[index]?.rate?.message && (
                                  <p className="text-sm text-destructive">
                                    {
                                      errors.employees[index].rate
                                        .message as string
                                    }
                                  </p>
                                )}
                              </div>

                              <div className="space-y-2">
                                <Label>Total</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  {...register(`employees.${index}.total`, {
                                    valueAsNumber: true,
                                  })}
                                  placeholder="0"
                                  readOnly
                                  className="bg-muted"
                                />
                                {errors.employees?.[index]?.total?.message && (
                                  <p className="text-sm text-destructive">
                                    {
                                      errors.employees[index].total
                                        .message as string
                                    }
                                  </p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Employee Notes</Label>
                        <Textarea
                          {...register(`employees.${index}.notes`)}
                          placeholder="Enter employee-specific notes"
                          rows={2}
                        />
                        {errors.employees?.[index]?.notes?.message && (
                          <p className="text-sm text-destructive">
                            {errors.employees[index].notes.message as string}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {errors.employees?.message && (
                <p className="text-sm text-destructive">
                  {errors.employees.message as string}
                </p>
              )}

              <div className="flex items-center justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={addEmployee}
                  disabled={!canAddMore}
                  className="w-full"
                >
                  <Plus className="h-5 w-5" />
                  Add Employee
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createOvertimeMutation.isPending}>
              {createOvertimeMutation.isPending
                ? "Creating..."
                : "Create Overtime Entry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
